// components/HeaderControls.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import { FaUndo, FaRedo } from 'react-icons/fa';
import ImportButton from './ImportButton';
import ColorPickerButton from './fragments/ColorPickerButton'

const HeaderControls = ({
  colorOptions,
  isDragging,
  setIsDragging,
  setSelectedNode,
  setSelectedEdge,
  addNode,
  setNodes,
  setEdges,
  minimapState,
  setMinimapState,
  controlsState,
  setControlsState,
  gridState,
  setGridState,
  setCanvasColor,
  canvasColor,
  onImport,
  onExport,
  onUndo,
  onRedo,
  undoHistory,
  undoPosition,
  isLocked,
  resetState
}) => {
  const canUndo = undoPosition >= 0;
  const canRedo = undoPosition < undoHistory.length - 1;

  // Access the ReactFlow instance
  const { screenToFlowPosition } = useReactFlow();

  // Configure Canvas Color
  const handleCanvasColor = (key, value) => {
    setCanvasColor(value);
  };

  // Memoize handlers to prevent unnecessary re-renders
  const clearWorkspace = useCallback(() => {
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
    setSelectedEdge(null);
  }, [setNodes, setEdges, setSelectedNode, setSelectedEdge]);
  
  // Drag and Drop Node
  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, [setIsDragging]);

  const handleMouseMove = useCallback((event) => {
    // Only handle if dragging
    if (!isDragging) return;
  }, [isDragging]);

  const handleMouseUp = useCallback((event) => {
    if (!isDragging) return;
    
    setIsDragging(false);
    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });
    addNode(position);
  }, [isDragging, setIsDragging, screenToFlowPosition, addNode]);


  useEffect(() => {
    if (!isDragging) {
      document.body.style.cursor = 'default';
      return;
    }

    document.body.style.cursor = 'grabbing';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = 'default';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const headerStyling = {
    // appHeaderContainer: "z-50 flex border-b-2 bg-gray-900 border-gray-600 text-sm text-white font-medium",
    headerButtonInner: "text-center py-1 w-24 text-sm border-r border-gray-500 select-none cursor-pointer hover:text-white hover:font-bold transition-colors duration-200",
    dragNode: `text-center py-1 w-28 text-sm border-r border-gray-500 select-none transition-colors duration-200 ${isLocked ? 'bg-gray-600 text-gray-300' : 'animatePurpleColorPulse hover:text-white hover:font-bold cursor-pointer'}`,
    undoRedo: "w-12 flex justify-center items-center border-r border-gray-500"
  }

  return (
    <>
        {/* HEADER */}
        <div className='flex flex-row flex-grow justify-between'>

          <div className='flex'>

            <div className={`${headerStyling.dragNode} w-40 border-l border-r `}
              onMouseDown={handleMouseDown}
            >
              Drag Node
            </div>

            <button className={`${headerStyling.undoRedo} ${canUndo ? "bg-[#200457] hover:bg-[#2c067a]" : "bg-gray-600 text-gray-300"} `}
              onClick={onUndo}
              title={canUndo ? "Undo Position" : "No Position to Undo"}
              disabled={!canUndo}
            >
              {/* <UndoIcon /> */}
              <FaUndo />
            </button>
            <button className={`${headerStyling.undoRedo} ${canRedo ? "bg-[#200457] hover:bg-[#2c067a]" : "bg-gray-600 text-gray-300"}`}
              onClick={onRedo}
              title={canUndo ? "Redo Position" : "No Position to Redo"}
              disabled={!canRedo}
            >
              <FaRedo />
              {/* <RedoIcon /> */}
            </button>

            <ColorPickerButton
                position={'bottom'}
                handleChange={handleCanvasColor}
                colorOptions={colorOptions}
                property="style.backgroundColor"
                currentColor={canvasColor}
                />

            {/* GRID State */}
            <button
              className={`border-l ${headerStyling.headerButtonInner} ${gridState ? 'bg-gray-800' : 'bg-gray-600 text-gray-300'}`}
              onClick={() => setGridState(!gridState)}
              title="Show / Hide Grid"
            >
              Grid
            </button>

            {/* MINI MAP */}
            <button
              className={`${headerStyling.headerButtonInner} ${minimapState ? 'bg-gray-800' : 'bg-gray-600 text-gray-300'}`}
              onClick={() => setMinimapState(!minimapState)}
              title="Show / Hide Minimap"
            >
              Minimap
            </button>

            {/* CONTROLS STATE */}
            <button
              className={`${headerStyling.headerButtonInner} ${controlsState ? 'bg-gray-800' : 'bg-gray-600 text-gray-300'}`}
              onClick={() => setControlsState(!controlsState)}
            >
              {controlsState ? 'Controls' : 'Controls'}
            </button>
            
            <ImportButton
              className={`${headerStyling.headerButtonInner} bg-gray-800 hover:bg-gray-900`}
              // onImport={handleImport}
              onImport={onImport}
              title="Import Flow JSON"
            />

            <button
              className={`${headerStyling.headerButtonInner} bg-gray-800 hover:bg-gray-900`}
              onClick={onExport}
              title="Export Flow JSON"
            >
              Export Flow
            </button>
          
          </div>
            
          {/* Clear Workspace Button */}
          <div className='flex'>
            <button
              className={`${headerStyling.headerButtonInner} bg-[#600000] text-gray-200 border-l hover:border-red-700"`}
              title="Clear Workspace"
              onClick={() => {
                clearWorkspace();
                resetState();
              }}
            >
              Clear
            </button>
          </div>
            
        </div>

    </>
  );
};

export default React.memo(HeaderControls);